import React, { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const dropdownClasses = {
  caption_dropdowns: "flex items-center justify-center gap-2",
  dropdown: "absolute inset-0 w-full opacity-0 cursor-pointer z-10",
  dropdown_month: "relative inline-flex items-center",
  dropdown_year: "relative inline-flex items-center",
  vhidden: "sr-only",
  caption_label: "text-sm font-medium inline-flex items-center gap-1 px-2 py-1 rounded-md",
};

const toDate = (v) => {
  if (!v) return undefined;
  const d = parse(v.slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
};

// value/onChange mimic a native input: onChange({ target: { value: "yyyy-MM-dd" } })
export const DatePicker = ({ value, onChange, placeholder = "Pick a date", className = "", style, fromYear = 1920, toYear = 2040, ...rest }) => {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`flex items-center gap-2 text-left ${className}`} style={style} data-testid={rest["data-testid"]}>
          <CalendarDays className="w-4 h-4 shrink-0" style={{ color: "var(--mf-primary)" }} />
          <span style={{ color: selected ? "var(--mf-text)" : "var(--mf-text-muted)" }}>{selected ? format(selected, "MMM d, yyyy") : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start" data-testid={`${rest["data-testid"]}-popover`}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          classNames={dropdownClasses}
          onSelect={(d) => { if (d) { onChange({ target: { value: format(d, "yyyy-MM-dd") } }); setOpen(false); } }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const label12 = (t) => format(parse(t, "HH:mm", new Date()), "h:mm a");

// value format "yyyy-MM-ddTHH:mm" (same as datetime-local)
export const DateTimePicker = ({ value, onChange, className = "", style, ...rest }) => {
  const [date, time] = value ? value.split("T") : ["", ""];
  const emit = (d, t) => onChange({ target: { value: d ? `${d}T${t || "09:00"}` : "" } });
  const tid = rest["data-testid"];
  const t = time ? time.slice(0, 5) : "";
  const opts = t && !TIMES.includes(t) ? [...TIMES, t].sort() : TIMES;
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2">
      <DatePicker value={date} onChange={(e) => emit(e.target.value, time)} className={className} style={style} fromYear={2020} toYear={2040} data-testid={`${tid}-date`} />
      <select value={t} onChange={(e) => emit(date || format(new Date(), "yyyy-MM-dd"), e.target.value)} className="px-3 py-2.5 rounded-xl outline-none text-sm" style={style} data-testid={`${tid}-time`}>
        <option value="">Time</option>
        {opts.map((o) => <option key={o} value={o}>{label12(o)}</option>)}
      </select>
    </div>
  );
};
